import React, { useState, useContext } from "react";
import { AuthContext } from "./Admin";
import { api } from "../../api";

const ChangePassword = () => {
    const { user, logout } = useContext(AuthContext);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const validateForm = () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            setError("All fields are required");
            return false;
        }

        if (newPassword !== confirmPassword) {
            setError("New passwords don't match");
            return false;
        }

        if (newPassword.length < 8) {
            setError("New password must be at least 8 characters long");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setMessage(null);

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await api.post("/cms/change-password", {
                current_password: currentPassword,
                new_password: newPassword
            });

            setMessage("Password changed successfully");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err) {
            if (err.response) {
                const status = err.response.status;
                if (status === 400) {
                    setError("Incorrect current password");
                } else if (status === 401) {
                    setError("Your session has expired. Please log in again.");
                    setTimeout(() => {
                        logout();
                    }, 2000);
                } else if (status === 404) {
                    setError("User not found");
                } else {
                    setError("An error occurred. Please try again later.");
                }
            } else {
                setError("Network error. Please check your connection.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="change-password">
            <div className="editor-header">
                <h2>Change Password</h2>
            </div>
            
            <div className="editor-container">
                {message && <div className="success-message">{message}</div>}
                {error && <div className="error-message">{error}</div>}
                
                <form onSubmit={handleSubmit} className="editor-form">
                    <div className="form-section">
                        <div className="form-group">
                            <label htmlFor="currentPassword">Current Password</label>
                            <input
                                type="password"
                                id="currentPassword"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                disabled={isLoading}
                                className="form-control"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="newPassword">New Password</label>
                            <input
                                type="password"
                                id="newPassword"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={isLoading}
                                className="form-control"
                            />
                            <small className="field-info">Password must be at least 8 characters long</small>
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm New Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={isLoading}
                                className="form-control"
                            />
                        </div>
                    </div>
                    
                    <div className="form-actions">
                        <button type="submit" className="button primary-button" disabled={isLoading}>
                            {isLoading ? "Changing Password..." : "Change Password"}
                        </button>
                    </div>
                </form>
            </div>
            
            <style jsx>{`
                .change-password {
                    max-width: 600px;
                    margin: 0 auto;
                    padding: 20px;
                }
                
                .editor-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    border-bottom: 1px solid #eee;
                    padding-bottom: 10px;
                }
                
                .editor-container {
                    background-color: white;
                    border-radius: 8px;
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
                }
                
                .editor-form {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }
                
                .form-section {
                    border: 1px solid #eee;
                    border-radius: 8px;
                    padding: 20px;
                    background-color: #fcfcfc;
                }
                
                .form-group {
                    margin-bottom: 15px;
                }
                
                .form-group label {
                    display: block;
                    margin-bottom: 5px;
                    font-weight: 500;
                    color: #333;
                }
                
                .form-control {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                    transition: border-color 0.3s;
                }
                
                .form-control:focus {
                    border-color: #2196F3;
                    outline: none;
                    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
                }
                
                .success-message {
                    background-color: #e8f5e9;
                    color: #388e3c;
                    padding: 12px;
                    border-radius: 4px;
                    margin-bottom: 20px;
                    border-left: 4px solid #4caf50;
                }
                
                .error-message {
                    background-color: #ffebee;
                    color: #d32f2f;
                    padding: 12px;
                    border-radius: 4px;
                    margin-bottom: 20px;
                    border-left: 4px solid #f44336;
                }
                
                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 15px;
                    margin-top: 10px;
                }
                
                .button {
                    padding: 10px 18px;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 16px;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                
                .primary-button {
                    background-color: #4caf50;
                    color: white;
                }
                
                .primary-button:hover {
                    background-color: #43a047;
                    transform: translateY(-1px);
                    box-shadow: 0 3px 5px rgba(0,0,0,0.15);
                }
                
                .primary-button:disabled {
                    background-color: #a5d6a7;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }
                
                .field-info {
                    display: block;
                    font-size: 12px;
                    color: #666;
                    margin-top: 4px;
                }
            `}</style>
        </div>
    );
};

export default ChangePassword; 